import { RefObject, useCallback, useEffect, useMemo } from 'react'
import { Flex, Box } from 'theme-ui'
import { Formik, Form, FormikProps } from 'formik'
import { lazy, object } from 'yup'
import { Heading, Button, Loading, Text, AccountActions } from 'components'
import { BasicTeacherSchema } from 'components/Form'
import {
  ConfigUtils,
  TeacherContact,
  TeacherData,
  TeacherUpdateFields,
  TeacherUpdateFormValues,
  UserUpdateFormValues,
  ViewMode,
} from 'types'
import { getChangedValues } from 'utils/helpers'
import { TEACHER_UPDATE } from '../../constants'
import {
  BasicUserSection,
  OrganizationDetailsSection,
  TeacherDetailsSection,
} from 'components/FormSections'

export type TeacherProfileProps = {
  user: TeacherData
  isSupport?: boolean
  editMode?: boolean
  disabled?: boolean
  loading?: boolean
  responseError?: string
  configUtils: ConfigUtils
  viewMode: ViewMode
  formikRef: RefObject<FormikProps<UserUpdateFormValues>>
  isUserUsingNewOrgs: boolean
  onSubmitForm: (values: TeacherUpdateFormValues) => void
  onLeaveForm: () => void
  onEnterEditing: () => void
  onCancelEditing: () => void
}

function getUpdateTeacherValues({
  initialValues,
  values,
}: {
  initialValues: UserUpdateFormValues
  values: UserUpdateFormValues
}): Partial<TeacherUpdateFormValues> {
  const updatedValues = getChangedValues<UserUpdateFormValues>({
    initialValues,
    values,
  })

  // we never send these with updates
  delete updatedValues.__country
  delete updatedValues.__defaultTeachingOrg
  delete updatedValues.__defaultTeachingOrgId
  return updatedValues
}

function hasChanged({
  initialValues,
  values,
}: {
  initialValues: UserUpdateFormValues
  values: UserUpdateFormValues
}): boolean {
  const changed = getUpdateTeacherValues({ initialValues, values })
  return !!Object.keys(changed).length
}

const isDisabledField = (
  fieldName: keyof UserUpdateFormValues,
  isSupport: boolean
): boolean => {
  if (!isSupport && fieldName === 'email') {
    return true
  } else {
    return false
  }
}

export function TeacherProfile({
  isSupport = false,
  user,
  responseError,
  configUtils,
  disabled = false,
  loading = false,
  viewMode,
  formikRef,
  isUserUsingNewOrgs,
  onSubmitForm,
  onLeaveForm,
  onEnterEditing,
  onCancelEditing,
}: TeacherProfileProps) {
  const initialValues = useMemo(
    () =>
      ({
        ...user,
        contact: user.contact ? user.contact : { default: {} },
      } as UserUpdateFormValues & { contact: TeacherContact }),
    [user]
  )
  const trainingStatus =
    user?.lmsRefresherCourseData?.status ?? user?.lmsCourseData?.status
  useEffect(() => {
    if (viewMode === 'edit') {
      formikRef.current?.validateForm()
    }
  }, [formikRef, viewMode])

  const submitUpdate = useCallback(
    (values) => {
      const { id } = values
      // Update Teacher
      const updatedValues = getUpdateTeacherValues({
        initialValues,
        values,
      })

      if (Object.keys(updatedValues).length) {
        updatedValues.id = id
        //reset teaching org if country is changed to a country without teaching org
        if (updatedValues.countryCode && !isUserUsingNewOrgs) {
          if (!configUtils.getTeachingOrgs(updatedValues.countryCode).length) {
            updatedValues.defaultTeachingOrg = undefined
          }
        }

        onSubmitForm(updatedValues as UserUpdateFormValues)
      }
    },
    [onSubmitForm, initialValues, configUtils, isUserUsingNewOrgs]
  )

  const isEditable = useCallback(
    (fieldName: keyof UserUpdateFormValues) => {
      if (viewMode === 'edit') {
        return !isDisabledField(fieldName, isSupport)
      } else if (viewMode === 'view') {
        return false
      }
    },
    [isSupport, viewMode]
  )

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values: UserUpdateFormValues, { setSubmitting }) => {
        if (viewMode === 'edit') {
          submitUpdate(values)
        } else {
          onEnterEditing()
          setSubmitting(false)
        }
      }}
      validationSchema={() => {
        return lazy(() => {
          if (viewMode === 'view') {
            return object({})
          } else {
            const schema = BasicTeacherSchema
            return schema
          }
        })
      }}>
      {({
        values,
        isSubmitting,
        handleSubmit,
      }: FormikProps<UserUpdateFormValues>) => {
        return (
          <Form onSubmit={handleSubmit} autoComplete="off" autoCorrect="off">
            <Flex sx={{ minHeight: 130 }} variant={`forms.section.${viewMode}`}>
              <BasicUserSection
                isSupport={isSupport}
                viewMode={viewMode}
                isDisabledField={(field) => !isEditable(field)}
                configUtils={configUtils}
                pageContext={TEACHER_UPDATE}
                startTabIndex={1}
              />

              <OrganizationDetailsSection
                isUserUsingNewOrgs={isUserUsingNewOrgs}
                pageContext={TEACHER_UPDATE}
                viewMode={viewMode}
                configUtils={configUtils}
                isDisabledField={(field) =>
                  !isEditable(field as TeacherUpdateFields)
                }
                startTabIndex={11}
              />
            </Flex>
            <TeacherDetailsSection
              viewMode={viewMode}
              switchToEditing={onEnterEditing}
              isDisabledField={(field) => !isEditable(field)}
              startTabIndex={21}
            />

            {values.id && (
              <>
                <Box variant={`forms.section.header`}>
                  <Heading
                    variant="forms.h2"
                    sx={{ display: 'inline-block', mt: 40 }}>
                    Account Actions
                  </Heading>
                </Box>
                <AccountActions
                  userId={values.id}
                  device={values.device}
                  disabled={disabled}
                  isCustomerSupportUser={isSupport}
                  userType={user.userType}
                  trainingStatus={trainingStatus}
                  viewMode={viewMode}
                  studentStatus={user.status}
                />
              </>
            )}
            <Box>
              <Text sx={{ fontWeight: 'heading', color: '#f18aa3' }}>
                {responseError}
              </Text>
            </Box>

            <Flex variant="forms.buttons.container" sx={{ mt: 60, mb: 68 }}>
              <Button
                variant="forms.buttons.primary"
                type="submit"
                data-cy="submit"
                disabled={
                  disabled ||
                  isSubmitting ||
                  (viewMode === 'edit' &&
                    !hasChanged({ values, initialValues }))
                }>
                {loading ? (
                  <Loading />
                ) : viewMode === 'view' ? (
                  <span>Edit</span>
                ) : (
                  <span>Save</span>
                )}
              </Button>
              <Button
                variant="forms.buttons.secondary"
                type="button"
                data-cy="cancel"
                disabled={disabled}
                onClick={viewMode === 'view' ? onLeaveForm : onCancelEditing}>
                {viewMode === 'view' ? 'Back' : 'Cancel'}
              </Button>
            </Flex>
          </Form>
        )
      }}
    </Formik>
  )
}
